import { useCallback, useMemo } from 'react';
import { ProvisionerTask, ProvisionerTaskStatus } from '@tanium-cloud/tsp-contracts';
import { getApiClient } from '../apiClient';
import { parseAxiosError } from '../utilities/commonFuncs';
import { Api } from '../api';

const isTaskStillRunning = (status: ProvisionerTaskStatus) => {
  return (
    status !== ProvisionerTaskStatus.Success &&
    status !== ProvisionerTaskStatus.Failure &&
    status !== ProvisionerTaskStatus.Timeout
  );
};

/**
 * Will return a promise that won't resolve until the /api-public/tasks/${task.id}/status API
 * returns with a status of Success, Failure, or Timeout
 * @task ProvisionerTask that should be poll
 * @return Promise that resolves when the Task has finished running or failed
 */
export const awaitTaskCompletion = (task: ProvisionerTask): Promise<ProvisionerTask> => {
  return new Promise<ProvisionerTask>((resolve, reject) => {
    if (!isTaskStillRunning(task.status)) {
      resolve(task);
      return;
    }

    const checkTaskStatusInterval = setInterval(() => {
      Api.task
        .getStatus(task.id)
        .then((res) => {
          if (!isTaskStillRunning(res.status)) {
            resolve(res);
            clearInterval(checkTaskStatusInterval);
          }
        })
        .catch((err) => {
          clearInterval(checkTaskStatusInterval);
          reject(parseAxiosError(err));
        });
    }, 3000);
  });
};

/**
 * Returns a callback function that will call the API and resolve once the task has finished or failed
 *
 * @url the URL provided must run a task and return a ProvisionerTask object
 */
export const useAsyncTaskCallback = (url: string): ((payload: Record<string, unknown>) => Promise<ProvisionerTask>) => {
  const apiClient = useMemo(() => getApiClient(), []);
  return useCallback(
    (payload: Record<string, unknown>): Promise<ProvisionerTask> => {
      return new Promise<ProvisionerTask>((resolve, reject) => {
        apiClient
          .put<ProvisionerTask>(url, payload)
          .then(async (response) => {
            const task = await awaitTaskCompletion(response.data);
            resolve(task);
          })
          .catch((err: any) => {
            reject(parseAxiosError(err));
          });
      });
    },
    [apiClient, url]
  );
};
